import { InjectionToken, ModuleWithProviders, } from '@angular/core';
import { Store } from '@ngrx/store';
import * as Rx from 'rxjs';
import { filter, map, switchMap, tap, withLatestFrom } from 'rxjs/operators';
import { ApiHttp } from './api-http.service';
import { assertNever, isDefined } from './helpers';
import { DatastoreMissingModuleError } from './missing-module-error';
import { pluckItemFromRawStore } from './store.helpers';
export var BACKEND_DEFAULT_BATCH_SIZE = 100;
var StoreBackend = /** @class */ (function () {
    function StoreBackend(store$, apiHttp) {
        this.store$ = store$;
        this.apiHttp = apiHttp;
        this.backendConfigs = {};
    }
    StoreBackend.prototype.defaultOrder = function (collection) {
        var config = this.backendConfigs[collection];
        if (!config) {
            throw new DatastoreMissingModuleError(collection);
        }
        return Array.isArray(config.defaultOrder)
            ? config.defaultOrder
            : [config.defaultOrder];
    };
    StoreBackend.prototype.batchSize = function (ref) {
        var config = this.backendConfigs[ref.path.collection];
        return (config && config.maxBatchSize) || BACKEND_DEFAULT_BATCH_SIZE;
    };
    StoreBackend.prototype.fetch = function (ref) {
        var _this = this;
        var path = ref.path, query = ref.query, order = ref.order;
        var type = path.collection;
        return Rx.of(this.backendConfigs[type]).pipe(map(function (config) {
            if (!config) {
                throw new DatastoreMissingModuleError(type);
            }
            return config.fetch;
        }), filter(isDefined), map(function (fetch) { return fetch(path.authUid, path.ids, query, order); }), switchMap(function (request) { return _this.apiHttp.fetch(request, query); }));
    };
    StoreBackend.prototype.search = function (collection, query$) {
        var _this = this;
        return query$.pipe(map(function (q) {
            var config = _this.backendConfigs[collection];
            if (!config) {
                throw new DatastoreMissingModuleError(collection);
            }
            return config.search(q);
        }), switchMap(function (request) { return _this.apiHttp.fetch(request); }), map(function (response) {
            return (response.status === 'success'
                ? response.result
                : []);
        }));
    };
    StoreBackend.prototype.push = function (ref, object, extra) {
        var _this = this;
        var path = ref.path, query = ref.query;
        var type = path.collection;
        return Rx.of(this.backendConfigs[type]).pipe(map(function (config) {
            if (!config) {
                throw new DatastoreMissingModuleError(type);
            }
            return config.push;
        }), filter(isDefined), map(function (push) {
            var request = push(path.authUid, object, extra);
            return {
                request: request,
                payload: {
                    type: type,
                    ref: ref,
                    object: object,
                    rawRequest: request.payload,
                },
            };
        }), tap(function (_a) {
            var payload = _a.payload;
            var action = { type: "API_PUSH", payload: payload };
            _this.store$.dispatch(action);
        }), switchMap(function (_a) {
            var request = _a.request, payload = _a.payload;
            return _this.apiHttp.post(request).pipe(map(function (result) { return ({ payload: payload, result: result }); }));
        }), map(function (_a) {
            var payload = _a.payload, result = _a.result;
            return _this.sendActions({ type: 'PUSH', payload: payload }, path, query, result);
        }));
    };
    StoreBackend.prototype.set = function (ref, id, object) {
        var _this = this;
        var path = ref.path, query = ref.query;
        var type = path.collection;
        return Rx.of(this.backendConfigs[type]).pipe(map(function (config) {
            if (!config) {
                throw new DatastoreMissingModuleError(type);
            }
            return config.set;
        }), filter(isDefined), withLatestFrom(this.store$), map(function (_a) {
            var set = _a[0], storeState = _a[1];
            var originalObject = pluckItemFromRawStore(storeState, path, id);
            if (originalObject === undefined) {
                throw new Error('Missing original object');
            }
            var request = set(path.authUid, object);
            return {
                request: request,
                payload: {
                    type: type,
                    ref: ref,
                    object: object,
                    rawRequest: request.payload,
                    originalObject: originalObject,
                },
            };
        }), tap(function (_a) {
            var payload = _a.payload;
            var action = { type: "API_SET", payload: payload };
            _this.store$.dispatch(action);
        }), switchMap(function (_a) {
            var request = _a.request, payload = _a.payload;
            return _this.apiHttp.post(request).pipe(map(function (result) { return ({ payload: payload, result: result }); }));
        }), map(function (_a) {
            var payload = _a.payload, result = _a.result;
            return _this.sendActions({ type: 'SET', payload: payload }, path, query, result);
        }));
    };
    StoreBackend.prototype.update = function (ref, id, delta) {
        var _this = this;
        var path = ref.path;
        var type = path.collection;
        return Rx.of(this.backendConfigs[type]).pipe(map(function (config) {
            if (!config) {
                throw new DatastoreMissingModuleError(type);
            }
            return config.update;
        }), filter(isDefined), withLatestFrom(this.store$), map(function (_a) {
            var update = _a[0], storeState = _a[1];
            var originalObject = pluckItemFromRawStore(storeState, path, id);
            if (originalObject === undefined) {
                throw new Error('Missing original object');
            }
            var request = update(path.authUid, delta, originalObject);
            return {
                request: request,
                payload: {
                    type: type,
                    ref: ref,
                    delta: delta,
                    rawRequest: request.payload,
                    originalObject: originalObject,
                },
            };
        }), tap(function (_a) {
            var payload = _a.payload;
            var action = { type: "API_UPDATE", payload: payload };
            _this.store$.dispatch(action);
        }), switchMap(function (_a) {
            var request = _a.request, payload = _a.payload;
            switch (request.method) {
                case 'POST':
                    return _this.apiHttp
                        .post(request)
                        .pipe(map(function (result) { return ({ payload: payload, result: result }); }));
                case 'PUT':
                    return _this.apiHttp
                        .put(request)
                        .pipe(map(function (result) { return ({ payload: payload, result: result }); }));
                default:
                    return assertNever(request.method);
            }
        }), map(function (_a) {
            var payload = _a.payload, result = _a.result;
            return _this.sendActions({ type: 'UPDATE', payload: payload }, payload.ref.path, payload.ref.query, result);
        }));
    };
    StoreBackend.prototype.delete = function (ref, id) {
        var _this = this;
        var path = ref.path;
        var type = path.collection;
        return Rx.of(this.backendConfigs[type]).pipe(map(function (config) {
            if (!config) {
                throw new DatastoreMissingModuleError(type);
            }
            return config.remove;
        }), filter(isDefined), withLatestFrom(this.store$), map(function (_a) {
            var del = _a[0], storeState = _a[1];
            var originalObject = pluckItemFromRawStore(storeState, path, id);
            if (originalObject === undefined) {
                throw new Error('Missing original object');
            }
            var request = del(path.authUid, id, originalObject);
            return {
                request: request,
                payload: {
                    type: type,
                    ref: ref,
                    rawRequest: request.payload,
                    originalObject: originalObject,
                },
            };
        }), tap(function (_a) {
            var payload = _a.payload;
            var action = { type: "API_DELETE", payload: payload };
            _this.store$.dispatch(action);
        }), switchMap(function (_a) {
            var request = _a.request, payload = _a.payload;
            switch (request.method) {
                case 'POST':
                    return _this.apiHttp
                        .post(request)
                        .pipe(map(function (result) { return ({ payload: payload, result: result }); }));
                case 'PUT':
                    return _this.apiHttp
                        .put(request)
                        .pipe(map(function (result) { return ({ payload: payload, result: result }); }));
                case 'DELETE':
                    return _this.apiHttp
                        .delete(request)
                        .pipe(map(function (result) { return ({ payload: payload, result: result }); }));
                default:
                    return assertNever(request.method);
            }
        }), map(function (_a) {
            var payload = _a.payload, result = _a.result;
            return _this.sendActions({ type: 'DELETE', payload: payload }, payload.ref.path, payload.ref.query, result);
        }));
    };
    StoreBackend.prototype.sendActions = function (baseAction, path, query, data) {
        switch (data.status) {
            case 'success': {
                var action = baseAction.type === 'PUSH'
                    ? {
                        type: 'API_PUSH_SUCCESS',
                        payload: {
                            type: baseAction.payload.type,
                            ref: baseAction.payload.ref,
                            object: baseAction.payload.object,
                            rawRequest: baseAction.payload.rawRequest,
                            result: data.result,
                        },
                    }
                    : baseAction.type === 'SET'
                        ? {
                            type: 'API_SET_SUCCESS',
                            payload: {
                                type: baseAction.payload.type,
                                ref: baseAction.payload.ref,
                                object: baseAction.payload.object,
                                originalObject: baseAction.payload.originalObject,
                                rawRequest: baseAction.payload.rawRequest,
                                result: data.result,
                            },
                        }
                        : baseAction.type === 'UPDATE'
                            ? {
                                type: 'API_UPDATE_SUCCESS',
                                payload: {
                                    type: baseAction.payload.type,
                                    ref: baseAction.payload.ref,
                                    delta: baseAction.payload.delta,
                                    originalObject: baseAction.payload.originalObject,
                                    rawRequest: baseAction.payload.rawRequest,
                                    result: data.result,
                                },
                            }
                            : {
                                type: 'API_DELETE_SUCCESS',
                                payload: {
                                    type: baseAction.payload.type,
                                    ref: baseAction.payload.ref,
                                    originalObject: baseAction.payload.originalObject,
                                    rawRequest: baseAction.payload.rawRequest,
                                    result: data.result,
                                },
                            };
                this.store$.dispatch(action);
                return baseAction.type === 'PUSH'
                    ? {
                        status: 'success',
                        id: data.result && data.result.id,
                    }
                    : { status: 'success' };
            }
            default: {
                var action = baseAction.type === 'PUSH'
                    ? {
                        type: 'API_PUSH_ERROR',
                        payload: baseAction.payload,
                    }
                    : baseAction.type === 'SET'
                        ? {
                            type: 'API_SET_ERROR',
                            payload: baseAction.payload,
                        }
                        : baseAction.type === 'UPDATE'
                            ? {
                                type: 'API_UPDATE_ERROR',
                                payload: baseAction.payload,
                            }
                            : {
                                type: 'API_DELETE_ERROR',
                                payload: baseAction.payload,
                            };
                this.store$.dispatch(action);
                return data;
            }
        }
    };
    StoreBackend.prototype.addFeature = function (collectionName, requestFactory) {
        this.backendConfigs[collectionName] = requestFactory;
    };
    return StoreBackend;
}());
export { StoreBackend };
export var BACKEND_COLLECTIONS = new InjectionToken('Backend Collections');
export var BACKEND_CONFIGS = new InjectionToken('Backend Configs');
var BackendRootModule = /** @class */ (function () {
    function BackendRootModule() {
    }
    return BackendRootModule;
}());
export { BackendRootModule };
var BackendFeatureModule = /** @class */ (function () {
    function BackendFeatureModule(storeBackend, backendCollections, backendConfigs) {
        backendCollections.map(function (collectionName, index) {
            storeBackend.addFeature(collectionName, backendConfigs[index]); // FIXME: any type
        });
    }
    return BackendFeatureModule;
}());
export { BackendFeatureModule };
var BackendModule = /** @class */ (function () {
    function BackendModule() {
    }
    BackendModule.forRoot = function () {
        return {
            ngModule: BackendRootModule,
            providers: [StoreBackend],
        };
    };
    BackendModule.forFeature = function (collectionName, configFactory) {
        return {
            ngModule: BackendFeatureModule,
            providers: [
                {
                    provide: BACKEND_COLLECTIONS,
                    multi: true,
                    useValue: collectionName,
                },
                {
                    provide: BACKEND_CONFIGS,
                    multi: true,
                    useFactory: configFactory,
                },
            ],
        };
    };
    return BackendModule;
}());
export { BackendModule };
